import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulbOn } from '@fortawesome/pro-light-svg-icons/faLightbulbOn';

import React, { useCallback, useEffect, useState } from 'react';
import { catchError, debounceTime, distinctUntilChanged, map, mergeMap } from 'rxjs/operators';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { of, Subject } from 'rxjs';

import { FloatTextArea } from 'ui-components/components/float-text-area';
import { InlineErrorMessage } from 'ui-components/components/inline-error-message';

import { getParsedAddress as getParsedAddressObservable } from '../../../../../../../address/address.api';
import { SHIPMENT_DETAILS } from '../../../../../../../constants/field-ids';
import { UNV } from '../../../../../../../enums/address-verification-status';

import messages from '../messages';
import style from './edit-shipto-modal.module.less';

const DEBOUNCE_TIME = 300;
const pasteAddressSubject = new Subject().pipe(
  distinctUntilChanged((prev, curr) => {
    return prev === curr;
  }),
  debounceTime(DEBOUNCE_TIME),
  mergeMap((rawAddress) => {
    if (!rawAddress) {
      return of({ response: '' });
    }

    return getParsedAddressObservable(rawAddress).pipe(
      map((response) => {
        return { response };
      }),
      catchError(() => {
        return of({
          error: true,
        });
      }),
    );
  }),
);

const mapPasteAddressResponse = (response, setter) => {
  setter((prev) => ({
    ...prev,
    name: response.name || '',
    company: response.company || '',
    line1: response.line1 || '',
    line2: response.line2 || '',
    line3: response.line3 || '',
    city: response.city || '',
    state: response.state || '',
    postalCode: response.postalCode || '',
    countryCode: response.countryCode || '',
    phone: response.phone ?? prev.phone,
    verificationStatus: UNV,
  }));
};

const PasteAddressSection = ({ resetFormState, updateFormState }) => {
  const intl = useIntl();
  const [hasParsingError, setHasParsingError] = useState(false);
  const [rawPastedAddress, setRawPastedAddress] = useState('');

  const onParsingSuccess = useCallback(
    (response) => {
      setHasParsingError(false);
      if (response) {
        mapPasteAddressResponse(response, updateFormState);
        return;
      }

      resetFormState();
    },
    [updateFormState, resetFormState],
  );

  const onParsingFailure = useCallback(() => setHasParsingError(true), []);

  useEffect(() => {
    const subscription = pasteAddressSubject.subscribe(({ response, error }) => {
      if (error) {
        onParsingFailure();
        return;
      }
      onParsingSuccess(response);
    });
    return () => subscription.unsubscribe();
  }, [onParsingSuccess, onParsingFailure]);

  const onChange = useCallback((value) => {
    setRawPastedAddress(value);
    pasteAddressSubject.next(value);
  }, []);

  const placeholder = intl.formatMessage(messages.pasteAddressPlaceholder);

  return (
    <div className={style['paste-address']}>
      <div className={style['paste-address-header']}>
        <FontAwesomeIcon icon={faLightbulbOn} />
        <FormattedMessage {...messages.pasteAddressHeader} />
      </div>
      <div className={style['paste-address-description']}>
        <FormattedMessage {...messages.pasteAddressDescription} />
      </div>
      <label className={style['paste-address-country-label']} htmlFor={SHIPMENT_DETAILS.pasteAddress}>
        <FormattedMessage {...messages.pasteAddressLabel} />
      </label>
      <FloatTextArea
        hasError={hasParsingError}
        id={SHIPMENT_DETAILS.pasteAddress}
        onChange={onChange}
        placeholder={placeholder}
        value={rawPastedAddress}
      />
      {hasParsingError && (
        <InlineErrorMessage state='error'>
          <FormattedMessage {...messages.parseAddressErrorMessage} />
        </InlineErrorMessage>
      )}
    </div>
  );
};

PasteAddressSection.propTypes = {
  resetFormState: PropTypes.func.isRequired,
  updateFormState: PropTypes.func.isRequired,
};

export default PasteAddressSection;
